import type {
    BaseQueryApi,
    FetchArgs,
    FetchBaseQueryError,
    FetchBaseQueryMeta
} from '@reduxjs/toolkit/query/react';
import { fetchBaseQuery } from '@reduxjs/toolkit/query';
import { isServer } from 'tradera/utils/nextjs';
import { toLocalizedUrl } from 'tradera/utils/url';
import { getLanguage } from 'tradera/utils/language';
import type { QueryReturnValue } from '@reduxjs/toolkit/dist/query/baseQueryTypes';
import type { ResponseStatus } from 'tradera/state/services/types/webapi-auth-generated';
import { logger } from 'packages/logger';

// Module scope variables, only OK because
// the functions where these are used must
// never be used on server!
let _clientTokenPromise: Promise<unknown> | null = null;
let _memberTokenPromise: Promise<unknown> | null = null;

const throwIfServer = () => {
    if (isServer) {
        throw new Error(
            'There are module scope variables in this module and is for client side use only!!!'
        );
    }
};

const jsonRequestHeaders: Record<string, string> = {
    Accept: 'application/json',
    'X-Requested-With': 'XMLHttpRequest'
};

type ErrorResponseData = {
    responseStatus: ResponseStatus;
};

type ResponseType = QueryReturnValue<
    unknown,
    FetchBaseQueryError,
    FetchBaseQueryMeta
>;

const isMissingAuthToken = (response: ResponseType) => {
    const errorData = response.error?.data as ErrorResponseData | undefined;
    return (
        response.error &&
        response.error.status === 400 &&
        errorData?.responseStatus.errorCode === 'MissingAuthToken'
    );
};

const isInvalidOrExpiredAuthToken = (response: ResponseType) => {
    const errorData = response.error?.data as ErrorResponseData | undefined;
    return (
        response.error &&
        response.error.status === 401 &&
        errorData?.responseStatus?.errorCode === 'InvalidOrExpiredAuthToken'
    );
};

const baseQuery = fetchBaseQuery({
    baseUrl: '',
    prepareHeaders: (headers) => {
        Object.keys(jsonRequestHeaders).forEach((headerName) => {
            headers.set(headerName, jsonRequestHeaders[headerName]);
        });
        return headers;
    },
    credentials: 'include'
});

/**
 * Obtains a client token cookie.
 * @returns true if the client token has a refresh token
 */
const obtainClientTokenCookie = async () => {
    const response = await fetch('/api/webapi/auth/web/client/token', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json'
        }
    });
    const json = await response.json();
    return json.hasRefreshToken;
};

const obtainClientTokenOnce = async () => {
    throwIfServer();
    if (_clientTokenPromise) {
        return await _clientTokenPromise;
    }
    // eslint-disable-next-line better-mutation/no-mutation
    _clientTokenPromise = obtainClientTokenCookie();
    const hasRefreshToken = await _clientTokenPromise;
    // eslint-disable-next-line better-mutation/no-mutation
    _clientTokenPromise = null;
    return hasRefreshToken;
};

const obtainMemberTokenCookies = () =>
    fetch('/api/webapi/auth/web/member/token/renew', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json'
        }
    });

const obtainMemberTokenCookiesOnce = async () => {
    throwIfServer();
    if (_memberTokenPromise) {
        return await _memberTokenPromise;
    }
    // eslint-disable-next-line better-mutation/no-mutation
    _memberTokenPromise = obtainMemberTokenCookies();
    await _memberTokenPromise;
    // eslint-disable-next-line better-mutation/no-mutation
    _memberTokenPromise = null;
};

const frontendBaseQueryWebApiReauth = async (
    args: string | FetchArgs,
    api: BaseQueryApi,
    extraOptions: object
): Promise<QueryReturnValue> => {
    throwIfServer();

    const originalBaseQuery = () => baseQuery(args, api, extraOptions);
    let result = await originalBaseQuery();

    if (isMissingAuthToken(result) || isInvalidOrExpiredAuthToken(result)) {
        // Obtain client token
        const hasRefreshToken = await obtainClientTokenOnce();
        if (hasRefreshToken) {
            // Refresh member tokens
            await obtainMemberTokenCookiesOnce();
        }
        // Re-run original query with fresh tokens
        result = await originalBaseQuery();
    }

    return result;
};

const frontendBaseQueryTouchwebReauth = async (
    args: string | FetchArgs,
    api: BaseQueryApi,
    extraOptions: object
): Promise<QueryReturnValue> => {
    let result = await baseQuery(args, api, extraOptions);

    if (isMissingAuthToken(result) || isInvalidOrExpiredAuthToken(result)) {
        // try to get a new token
        const refreshResult = await baseQuery(
            toLocalizedUrl('/login/state', getLanguage()),
            api,
            extraOptions
        );
        if (refreshResult.data) {
            // retry the initial query
            result = await baseQuery(args, api, extraOptions);
            if (isInvalidOrExpiredAuthToken(result)) {
                logger(result);
            }
        } else {
            logger(result);
            window.location.reload();
        }
    }
    return result;
};

export const frontendBaseQueryWithReauth = async (
    input: string | FetchArgs,
    api: BaseQueryApi,
    extraOptions: object
): Promise<QueryReturnValue> => {
    throwIfServer();
    try {
        const state = api.getState() as {
            environment: { featureSwitches: Record<string, boolean> };
        };
        const isNextWebTokenHandlingEnabled =
            !!state.environment.featureSwitches['next-web-token-handling'];
        return isNextWebTokenHandlingEnabled
            ? frontendBaseQueryWebApiReauth(input, api, extraOptions)
            : frontendBaseQueryTouchwebReauth(input, api, extraOptions);
    } catch (error) {
        logger(error);
        // Base query and query functions must always catch errors themselves, and return it in an object!
        // https://redux-toolkit.js.org/rtk-query/usage/customizing-queries#implementing-a-custom-basequery
        return {
            error
        };
    }
};
